/** @jsxImportSource ./simboluri-jsx */
import { eDecizie, textVerdict } from "./elemente";
import DeciziiProiect from "./DeciziiProiect";
import { useEffect, useMemo, useState } from "react";
import { FemModel, FemPanou, StepState, fmtDate } from "./tipuri";
import { CotaH, CotaV, cmFmt } from "./cote";
import LocalizareCladire from "./LocalizareCladire";
import Info from "./Info";
import TitluVerificare, { useCatalogVerificari } from "./Verificare";
import { Focus } from "./legaturi";
import { DescriereaPasului } from "./TitluPas";
import NotePas from "./NotePas";
import { t, useT, type Cheie } from "../i18n";
import { traduceRezultat } from "./texteRezultat";

// Pasul 16 — Armarea plăcii. Pagina are TREI tabele, fiindcă verificările
// plăcii nu au aceeași granularitate («sau nu se verifică
// la nivel de ochi?»):
//
//   * pe OCHI — reazemele, grosimea, săgeata, încovoierea, constructivul:
//     ochiul e unitatea pe care gândește structuristul încovoierea;
//   * PUNCTUAL — consolele (balcoanele): au altă schemă statică, iar ancora
//     lor e consola, nu ochiul;
//   * pe NIVEL — transferul forței de la placă la pereții de beton: e o
//     verificare a diafragmei, nu a unui panou.
//
// Explicațiile verificărilor NU se scriu aici: vin din catalog_verificari.py
// prin <TitluVerificare>.

// E, R și R/E lipsesc la o verificare da/nu trecută (V0, «are pe ce
// descărca?») și la o decizie de proiect (`eDecizie`)
type Verificare = {
  nume: string;
  grupa: string;
  E: number | null;
  R: number | null;
  RE: number | null;
  verdict: string;
  acceptat_marja?: number | null;
  referinta?: string;
  punct?: string;
  unitate?: string;
  nota?: string | null;
  detaliu?: Record<string, unknown>;
};

type Plasa = {
  poz: string;
  phi_mm: number;
  pas_cm: number;
  as_cm2: number;
  as_nec_cm2?: number | null;
  as_min_cm2: number;
  d_cm: number;
  m_rd_knm_m: number;
  m_ed_knm_m: number;
  xi: number;
  text: string;
  imposibil?: boolean;
};

type Capat = {
  L_reazem_m: number;
  L_liber_m: number;
  rezemat: boolean;
  continuu: boolean;
  vecini: string[];
  t_reazem_m?: number | null;
};

type Directie = {
  l_m: number;
  i: Capat;
  j: Capat;
  n_continue: number;
  rezemat: boolean;
};

type FemMoment = {
  m_camp_knm_m: number;
  m_i_knm_m: number;
  m_j_knm_m: number;
  combinatie_camp?: string | null;
  L_taietura_m?: number;
};

type Moment = {
  k: number | null;
  q_kn_m2: number;
  l_m: number;
  l_env_m: number;
  n_continue: number;
  coef: number[];
  m_camp_knm_m: number;
  m_i_knm_m: number;
  m_j_knm_m: number;
  m_pereti_camp_knm_m: number;
  de_la_vecin?: number | null;
  formula: string;
  // tranșa 2: cele două metode, pe fiecare direcție
  sursa?: "fasii" | "fem";
  fem?: FemMoment;
  fasii?: { m_camp_knm_m: number; m_i_knm_m: number; m_j_knm_m: number };
  divergenta_camp_pct?: number | null;
};

type Strapungere = {
  id_plan: string;
  nivel: number;
  v_ed_kn: number;
  salt_axial_kn?: number;
  adus_de_grinzi_kn?: number;
  poz: string;
  beta: number;
  u1_m: number;
  d_cm: number;
  rho_pct: number;
  v_ed_mpa: number;
  v_rdc_mpa: number;
  v_ed_fata_mpa: number;
  v_rd_max_mpa: number;
  grinzi_la_nod: number;
};

type ZonaGol = {
  ochi?: string;
  nivel?: string;
  eticheta: string;
  arie_zona_m2: number;
  arie_pe_ochi_m2: number;
};

export type Ochi = {
  id: string;
  nivel: number;
  nivel_nume: string;
  cat: string;
  arie_m2: number;
  deschidere_m: number;
  lx_m: number;
  ly_m: number;
  h_cm: number;
  schema: {
    tip: string;
    dir_scurt: string;
    dir_lung: string;
    raport?: number | null;
    x: Directie;
    y: Directie;
  };
  incarcare: {
    q_uls_kn_m2: number;
    q_sls_kn_m2: number;
    cat: string;
    componente: {
      eticheta: string;
      valoare_kn_m2: number;
      formula: string;
      referinta: string;
    }[];
    linii: { L_m: number; q_kn_ml: number; q_ed_kn_ml: number; sursa: string }[];
    formula_uls: string;
    formula_sls: string;
  };
  momente: Record<string, Moment | null>;
  armare: Record<string, Plasa>;
  console: {
    tip: string;
    adanc_m: number;
    L_prindere_m: number;
    armare?: string | null;
    as_cm2?: number | null;
  }[];
  verificari: Verificare[];
  verdict: string;
  zone_gol?: ZonaGol[];
  istoric: { de_ce: string; de_la: string; la: string; re: number }[];
  otel_kg: number;
  beton_m3: number;
  note: string[];
};

export type Rezultat = {
  activ: boolean;
  motiv?: string;
  panouri: Ochi[];
  nr_panouri: number;
  nr_verificari: number;
  nr_picate: number;
  otel_total_kg: number;
  beton_total_m3: number;
  verdicte: Record<string, number>;
  transfer_diafragma: {
    activ: boolean;
    motiv?: string;
    nota?: string;
    tau_rd_kpa?: number;
    pereti: {
      element: string;
      nivel: string;
      L_m: number;
      V_ed_kn: number;
      tau_ed_kpa: number;
      tau_rd_kpa: number;
      as_conector_cm2_m: number;
    }[];
    verificari: Verificare[];
  };
  strapungere: {
    activ: boolean;
    motiv?: string;
    nota?: string;
    nr_sarite?: number;
    stalpi: Strapungere[];
    verificari: Verificare[];
  };
  metoda_momente?: {
    fem_activ: boolean;
    motiv?: string | null;
    nr_taieturi: number;
    nr_ochiuri_pe_fem: number;
    nota: string;
  };
  zone_gol?: ZonaGol[];
  parametri: Record<string, unknown>;
  ipoteze: { ce: string; de_ce: string; efect: string }[];
  nu_se_aplica: { nume: string; la: string; motiv: string; referinta: string }[];
  avertismente: string[];
};

function nr(v: number | undefined | null, zecimale = 1): string {
  if (v === undefined || v === null) return "—";
  return v.toFixed(zecimale).replace(".", ",");
}

function verdictClass(v: string): string {
  if (v.startsWith("SE VERIFICA")) return "st-ok";
  if (v.startsWith("ATENTIE")) return "st-warn";
  return "st-bad";
}

// Schema de rezemare a ochiului: CHEI, nu texte — tipul vine de la server cu
// codul lui, numele citibil e al interfeței.
const SCHEMA_LABEL: Record<string, Cheie> = {
  doua_directii: "struct.arpl.schema_doua_directii",
  una_directie: "struct.arpl.schema_una_directie",
  consola: "struct.arpl.schema_consola",
  nerezemat: "struct.arpl.schema_nerezemat",
};

function Capete({ d, nume }: { d: Directie; nume: string }) {
  return (
    <tr>
      <td>{nume}</td>
      <td>{nr(d.l_m, 2)} m</td>
      <td>{t("struct.arpl.din_2", { n: d.n_continue })}</td>
      {(["i", "j"] as const).map((k) => {
        const c = d[k];
        return (
          <td key={k}>
            {c.rezemat ? (
              <>
                {t("struct.arpl.reazem_m", { v: nr(c.L_reazem_m, 1) })}
                {c.continuu ? (
                  <span className="st-ok">{t("struct.arpl.continuu")}</span>
                ) : (
                  <span className="sg-muted">{t("struct.arpl.simplu")}</span>
                )}
                {c.vecini.length ? (
                  <span className="sg-muted">
                    {t("struct.arpl.vecini", { lista: c.vecini.join(", ") })}
                  </span>
                ) : null}
              </>
            ) : (
              <span className="st-bad">
                {t("struct.arpl.fara_reazem_liberi", { v: nr(c.L_liber_m, 1) })}
              </span>
            )}
          </td>
        );
      })}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// DESENELE ochiului — Pasul 16 arată acum ce arată paginile de stâlpi, grinzi
// și pereți: elementul desenat, nu doar cifrele lui.
// ---------------------------------------------------------------------------

export const CULOARE_REAZEM: Record<string, string> = {
  grinda: "#3d7cab",
  perete: "#459c8b",
  contur_fara_reazem: "#b5323d",
};

// Felul reazemului unei laturi: tot chei — codul vine din geometrie.
const ETICHETA_REAZEM: Record<string, Cheie> = {
  grinda: "struct.arpl.eticheta_grinda",
  perete: "struct.arpl.eticheta_perete",
  contur_fara_reazem: "struct.arpl.eticheta_fara_reazem",
};

// transformarea model → SVG: bbox-ul conturului, cu marginea de etichete și
// axa Y răsturnată (în plan, Y crește în sus; în SVG, în jos)
function cadru(pct: [number, number][], W: number, marja: number, Hmax = 430) {
  const xs = pct.map((p) => p[0]);
  const ys = pct.map((p) => p[1]);
  const x0 = Math.min(...xs), x1 = Math.max(...xs);
  const y0 = Math.min(...ys), y1 = Math.max(...ys);
  const dx = Math.max(x1 - x0, 0.5), dy = Math.max(y1 - y0, 0.5);
  // scara ține în ecran ȘI pe lățime ȘI pe înălțime: un ochi în picioare
  // (l_lung pe Y) desena altfel o pânză de 800 px, cu jumătatea de jos sub
  // marginea paginii
  const s = Math.min((W - 2 * marja) / dx, (Hmax - 2 * marja) / dy);
  const H = dy * s + 2 * marja;
  const W2 = dx * s + 2 * marja;
  return {
    W: W2, H, s,
    X: (x: number) => marja + (x - x0) * s,
    Y: (y: number) => H - marja - (y - y0) * s,
  };
}

function cale(pct: [number, number][], c: ReturnType<typeof cadru>): string {
  return pct.map((p, i) => `${i ? "L" : "M"}${c.X(p[0]).toFixed(1)},${c.Y(p[1]).toFixed(1)}`)
    .join(" ") + " Z";
}

// PLANUL ochiului: laturile colorate după reazem, plasa de jos desenată bară
// cu bară la pasul real, pereții care apasă pe placă, golurile și consolele.
export function PlanOchi({
  ochi, panou, W = 620, peVecin,
}: {
  ochi: Ochi;
  panou: FemPanou | null;
  W?: number;
  peVecin?: (id: string) => void;
}) {
  useT();
  const contur = (panou?.contur ?? []) as [number, number][];
  if (contur.length < 3) {
    return (
      <div className="sg-muted">
        {t("struct.arpl.conturul_ochiului_rezult")}
      </div>
    );
  }
  const marja = 46;
  const c = cadru(contur, W, marja);
  const laturi = panou?.laturi ?? [];
  const ds = ochi.schema.dir_scurt;
  const dl = ochi.schema.dir_lung;
  const jos_s = ochi.armare[`jos_${ds}`];
  const jos_l = ochi.armare[`jos_${dl}`];
  // barele de jos: pe direcția SCURTĂ stau dedesubt (ele duc momentul mare),
  // deci se desenează primele. Pasul e cel real; dacă ies prea multe bare
  // pentru lățimea desenului, se rărește desenul (nu armătura) și se spune.
  const bx = Math.min(...contur.map((p) => p[0]));
  const bX = Math.max(...contur.map((p) => p[0]));
  const by = Math.min(...contur.map((p) => p[1]));
  const bY = Math.max(...contur.map((p) => p[1]));
  function bare(pas_cm: number, orizontala: boolean) {
    const pas = pas_cm / 100;
    if (!(pas > 0.02)) return { linii: [] as [number, number, number, number][], rarit: 1 };
    const lungime = orizontala ? bY - by : bX - bx;
    let k = 1;
    while (lungime / (pas * k) > 60) k += 1;
    const linii: [number, number, number, number][] = [];
    for (let t = (orizontala ? by : bx) + pas * k / 2;
         t < (orizontala ? bY : bX); t += pas * k) {
      linii.push(orizontala ? [bx, t, bX, t] : [t, by, t, bY]);
    }
    return { linii, rarit: k };
  }
  // bara de pe direcția X e ORIZONTALĂ în plan
  const barX = bare(ochi.armare[`jos_x`]?.pas_cm ?? 0, false);
  const barY = bare(ochi.armare[`jos_y`]?.pas_cm ?? 0, true);
  const clip = `ochi-${ochi.id.replace(/\W/g, "")}`;
  return (
    <div>
      <svg viewBox={`0 0 ${c.W} ${c.H}`} className="ap-svg"
           style={{ width: "100%", maxWidth: c.W }}>
        <defs>
          <clipPath id={clip}>
            <path d={cale(contur, c)} />
          </clipPath>
          <pattern id={`${clip}-gol`} width="8" height="8"
                   patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="8" stroke="#a97614" strokeWidth="2" />
          </pattern>
        </defs>
        {/* placa */}
        <path d={cale(contur, c)} fill="#ececec" stroke="none" />
        {/* golurile (casa scării, liftul, ghenele) — decupate din placă */}
        {(panou?.zone_gol ?? []).map((z, i) =>
          (z.contur ?? []).length >= 3 ? (
            <path key={i} d={cale(z.contur as [number, number][], c)}
                  fill={`url(#${clip}-gol)`} stroke="#a97614" strokeWidth="1" />
          ) : null
        )}
        {/* inelele (goluri interioare ale unui ochi inelar) */}
        {(panou?.inele ?? []).map((inel, i) => (
          <path key={i} d={cale(inel.contur as [number, number][], c)}
                fill="#ffffff" stroke="#b5323d" strokeWidth="1.5"
                strokeDasharray="5 3" />
        ))}
        {/* plasa de jos, la pasul real */}
        <g clipPath={`url(#${clip})`} opacity="0.7">
          {barX.linii.map(([x1, y1, x2, y2], i) => (
            <line key={`x${i}`} x1={c.X(x1)} y1={c.Y(y1)} x2={c.X(x2)} y2={c.Y(y2)}
                  stroke="#b5323d" strokeWidth="1" />
          ))}
          {barY.linii.map(([x1, y1, x2, y2], i) => (
            <line key={`y${i}`} x1={c.X(x1)} y1={c.Y(y1)} x2={c.X(x2)} y2={c.Y(y2)}
                  stroke="#2e7d72" strokeWidth="1" />
          ))}
        </g>
        {/* pereții care stau PE ochi, fără grindă dedesubt */}
        {(panou?.pereti_pe_placa ?? []).map((s, i) => (
          <line key={i} x1={c.X(s.a[0])} y1={c.Y(s.a[1])}
                x2={c.X(s.b[0])} y2={c.Y(s.b[1])}
                stroke="#828282" strokeWidth="5" strokeLinecap="round"
                opacity="0.75" />
        ))}
        {/* laturile, colorate după reazem */}
        {laturi.map((l, i) => {
          const mx = (c.X(l.a[0]) + c.X(l.b[0])) / 2;
          const my = (c.Y(l.a[1]) + c.Y(l.b[1])) / 2;
          const orizontala = Math.abs(l.b[0] - l.a[0]) >= Math.abs(l.b[1] - l.a[1]);
          return (
            <g key={i}>
              <line x1={c.X(l.a[0])} y1={c.Y(l.a[1])}
                    x2={c.X(l.b[0])} y2={c.Y(l.b[1])}
                    stroke={CULOARE_REAZEM[l.reazem] ?? "#888"}
                    strokeWidth={l.reazem === "contur_fara_reazem" ? 3 : 5}
                    strokeDasharray={l.reazem === "contur_fara_reazem" ? "7 4" : undefined}
                    strokeLinecap="round" />
              {l.L_m >= 0.8 ? (
                <text x={mx} y={my + (orizontala ? -8 : 0)} fontSize="9"
                      textAnchor="middle" fill="#555"
                      style={{ cursor: l.vecin && peVecin ? "pointer" : undefined }}
                      onClick={() => l.vecin && peVecin?.(l.vecin)}>
                  {nr(l.L_m, 1)} m
                  {l.continua && l.vecin ? " " + t("struct.arpl.continuu_spre", { vecin: l.vecin }) : ""}
                </text>
              ) : null}
            </g>
          );
        })}
        {/* conturul, deasupra tuturor */}
        <path d={cale(contur, c)} fill="none" stroke="#232323" strokeWidth="1" />
        {/* gabaritul + deschiderea de calcul */}
        <text x={c.W / 2} y={c.H - 12} fontSize="10" textAnchor="middle" fill="#232323">
          {t("struct.arpl.gabarit_deschidere", {
            lx: nr(ochi.lx_m, 2), ly: nr(ochi.ly_m, 2),
            d: nr(ochi.deschidere_m, 2),
          })}
        </text>
        <text x={12} y={18} fontSize="11" fill="#232323">
          {ochi.id} — {ochi.nivel_nume}, {nr(ochi.arie_m2, 1)} m²
        </text>
      </svg>
      <div className="sg-muted" style={{ fontSize: 12, lineHeight: 1.5 }}>
        {t("struct.arpl.laturile")} <span style={{ color: CULOARE_REAZEM.grinda }}>{t("struct.arpl.grinda")}</span>{" "}
        <span style={{ color: CULOARE_REAZEM.perete }}>{t("struct.arpl.perete_sub_placa")}</span>{" "}
        <span style={{ color: CULOARE_REAZEM.contur_fara_reazem }}>{t("struct.arpl.fara_reazem")}</span>{" "}
        {t("struct.arpl.legenda_ochi")}{" "}
        <span style={{ color: "#b5323d" }}>{t("struct.arpl.rosu_x")}</span> (
        {ochi.armare["jos_x"]?.text ?? "—"}),{" "}
        <span style={{ color: "#2e7d72" }}>{t("struct.arpl.albastru_y")}</span> (
        {ochi.armare["jos_y"]?.text ?? "—"})
        {barX.rarit > 1 || barY.rarit > 1 ? (
          <>
            {t("struct.arpl.desenul_rarit", {
              n: Math.max(barX.rarit, barY.rarit),
            })}
          </>
        ) : null}
        {jos_s && jos_l ? (
          <>
            {t("struct.arpl.directia_scurta_e", {
              dir: ds.toUpperCase(),
              l: nr((ds === "x" ? ochi.schema.x : ochi.schema.y).l_m, 2),
            })}
          </>
        ) : null}
      </div>
    </div>
  );
}

// BARA R/E: cât de departe e verificarea de 1,00, la o privire. Linia
// verticală e pragul de acceptare al proiectului (marja de subdimensionare).
function BaraRE({ re, prag }: { re: number; prag: number }) {
  const v = Math.max(0, Math.min(2, re));
  const culoare = re >= 1 ? "#4f8207" : re >= prag ? "#a97614" : "#b5323d";
  return (
    <svg viewBox="0 0 120 10" style={{ width: 110, height: 10, display: "block" }}>
      <rect x="0" y="3" width="120" height="4" fill="#dedede" />
      <rect x="0" y="3" width={(v / 2) * 120} height="4" fill={culoare} />
      <line x1={60} y1="0" x2={60} y2="10" stroke="#232323" strokeWidth="1" />
      {prag < 1 ? (
        <line x1={(prag / 2) * 120} y1="1" x2={(prag / 2) * 120} y2="9"
              stroke="#a97614" strokeWidth="1" strokeDasharray="2 1" />
      ) : null}
    </svg>
  );
}

// SECȚIUNEA prin grosimea plăcii: cele două plase, acoperirile și brațele de
// pârghie cu care s-a calculat fiecare M_Rd.
export function SectiunePlaca({ ochi, acoperire_mm }: { ochi: Ochi; acoperire_mm: number }) {
  const W = 620, H = 268;
  const h = ochi.h_cm;
  // marginea stângă ține cota «h = … cm» întreagă (la 60 px eticheta ieșea
  // din desen), iar cea dreaptă cota lui d
  const marjaX = 118, latime = W - 2 * marjaX;
  const scaleZ = 90 / Math.max(h, 1);       // cm -> px
  const zSus = 60;
  const zJos = zSus + h * scaleZ;
  const ac = acoperire_mm / 10;
  const ds = ochi.schema.dir_scurt;
  const jos = ochi.armare[`jos_${ds}`];
  const sus = ochi.armare[`sus_${ds}`];
  const phiJos = (jos?.phi_mm ?? 8) / 10;
  const phiSus = (sus?.phi_mm ?? 8) / 10;
  const nBare = Math.min(14, Math.max(4, Math.round(latime / 44)));
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="ap-svg" style={{ width: "100%", maxWidth: W }}>
        <rect x={marjaX} y={zSus} width={latime} height={h * scaleZ}
              fill="#ececec" stroke="#232323" strokeWidth="1" />
        {/* barele: cerc pentru fiecare, la acoperirea reală */}
        {Array.from({ length: nBare }, (_, i) => {
          const x = marjaX + (latime * (i + 0.5)) / nBare;
          return (
            <g key={i}>
              <circle cx={x} cy={zJos - (ac + phiJos / 2) * scaleZ}
                      r={Math.max(2, (phiJos / 2) * scaleZ)} fill="#b5323d" />
              <circle cx={x} cy={zSus + (ac + phiSus / 2) * scaleZ}
                      r={Math.max(2, (phiSus / 2) * scaleZ)} fill="#6b4fa1" />
            </g>
          );
        })}
        {/* COTELE — aceleași primitive ca la celelalte elemente:
            grosimea, brațul de pârghie d, acoperirile de sus și de jos, plus
            deschiderile ochiului */}
        <CotaV x={marjaX - 26} y1={zSus} y2={zJos} martorX={marjaX}
               text={`h = ${cmFmt(h)} cm`} />
        <CotaV x={marjaX - 66} y1={zSus} y2={zSus + ac * scaleZ}
               martorX={marjaX} text={`c ${nr(ac, 1)}`} size={9} />
        <CotaV x={marjaX - 66} y1={zJos - ac * scaleZ} y2={zJos}
               martorX={marjaX} text={`c ${nr(ac, 1)}`} size={9} />
        {jos ? (
          <CotaV x={W - marjaX + 22} y1={zSus}
                 y2={zJos - (ac + phiJos / 2) * scaleZ}
                 martorX={W - marjaX} laDreapta culoare="#b5323d"
                 text={`d = ${nr(jos.d_cm, 1)} cm`} />
        ) : null}
        <CotaH x1={marjaX} x2={W - marjaX} y={zJos + 46} martorY={zJos}
               text={`L_${ds} = ${nr(ds === "x" ? ochi.lx_m : ochi.ly_m, 2)} m`} />
        <text x={marjaX} y={zSus - 12} fontSize="10" fill="#6b4fa1">
          {t("struct.arpl.sus_mrd", {
            text: sus?.text ?? "—", v: nr(sus?.m_rd_knm_m, 1),
          })}
        </text>
        <text x={marjaX} y={zJos + 22} fontSize="10" fill="#b5323d">
          {t("struct.arpl.jos_mrd", {
            text: jos?.text ?? "—", v: nr(jos?.m_rd_knm_m, 1),
          })}
        </text>
        <text x={6} y={H - 8} fontSize="10" fill="#737373">
          {t("struct.arpl.acoperirea_nominala", {
            c: nr(ac, 1), dir: ds.toUpperCase(),
          })}
        </text>
      </svg>
    </div>
  );
}

// MOMENTELE pe o direcție: schema statică (ce are la capete), diagrama și
// competiția fâșii ↔ FEM, cu metoda care a guvernat marcată.
function MomenteDirectie({ ochi, dir }: { ochi: Ochi; dir: "x" | "y" }) {
  useT();
  const m = ochi.momente[dir];
  const d = dir === "x" ? ochi.schema.x : ochi.schema.y;
  if (!m) {
    return (
      <div className="sg-muted">
        {t("struct.arpl.directia_nu_duce", { dir: dir.toUpperCase() })}
      </div>
    );
  }
  const W = 620, H = 210;
  const x0 = 70, x1 = W - 70;
  const linie = 78;
  const maxM = Math.max(Math.abs(m.m_camp_knm_m), Math.abs(m.m_i_knm_m),
                        Math.abs(m.m_j_knm_m), 1);
  const sc = 52 / maxM;
  const simbol = (rezemat: boolean, continuu: boolean, x: number) => {
    if (!rezemat) return <text x={x} y={linie + 26} fontSize="9" textAnchor="middle" fill="#b5323d">{t("struct.arpl.liber")}</text>;
    if (continuu) {
      return (
        <>
          <rect x={x - 7} y={linie - 9} width="14" height="18" fill="#3d7cab" opacity="0.25" />
          <text x={x} y={linie + 26} fontSize="9" textAnchor="middle" fill="#3d7cab">{t("struct.arpl.incastrat_continuu")}</text>
        </>
      );
    }
    return (
      <>
        <polygon points={`${x},${linie} ${x - 7},${linie + 11} ${x + 7},${linie + 11}`} fill="#3d7cab" />
        <text x={x} y={linie + 26} fontSize="9" textAnchor="middle" fill="#3d7cab">{t("struct.arpl.simplu_rezemat")}</text>
      </>
    );
  };
  // diagrama: parabola de câmp POZITIVĂ jos, momentele de reazem negative sus
  const camp = m.m_camp_knm_m * sc;
  const mi = m.m_i_knm_m * sc, mj = m.m_j_knm_m * sc;
  const cale_m = `M${x0},${linie - mi} Q${(x0 + x1) / 2},${linie + 2 * camp + (mi + mj) / 2} ${x1},${linie - mj}`;
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="ap-svg" style={{ width: "100%", maxWidth: W }}>
        <line x1={x0} y1={linie} x2={x1} y2={linie} stroke="#232323" strokeWidth="2" />
        {simbol(d.i.rezemat, d.i.continuu, x0)}
        {simbol(d.j.rezemat, d.j.continuu, x1)}
        <path d={cale_m} fill="none" stroke="#b5323d" strokeWidth="2" />
        {/* eticheta stă la VÂRFUL parabolei (apexul unui Bézier pătratic e
            la jumătatea offsetului punctului de control), nu la punctul de
            control — altfel, la momente mari, ieșea din desen */}
        <text x={(x0 + x1) / 2} y={Math.min(linie + camp + 20, H - 34)}
              fontSize="10" textAnchor="middle" fill="#b5323d">
          {t("struct.arpl.m_camp_diag", { v: nr(m.m_camp_knm_m, 2) })}
        </text>
        <text x={x0 - 4} y={linie - mi - 6} fontSize="10" textAnchor="end" fill="#6b4fa1">
          {nr(m.m_i_knm_m, 2)}
        </text>
        <text x={x1 + 4} y={linie - mj - 6} fontSize="10" fill="#6b4fa1">
          {nr(m.m_j_knm_m, 2)}
        </text>
        <text x={(x0 + x1) / 2} y={linie - 58} fontSize="10" textAnchor="middle" fill="#232323">
          l = {nr(m.l_m, 2)} m
          {m.de_la_vecin ? t("struct.arpl.reazemul_comun_ia", { p1: nr(m.de_la_vecin, 2) }) : ""}
        </text>
        {m.fem ? (
          <g>
            <text x={x0} y={H - 26} fontSize="10" fill="#737373">
              {t("struct.arpl.fasii_fem", {
                a: nr(m.fasii?.m_camp_knm_m, 2),
                b: `${nr(m.fem.m_camp_knm_m, 2)} kNm/m`,
              })}
            </text>
            <text x={x0} y={H - 10} fontSize="10"
                  fill={m.sursa === "fem" ? "#a97614" : "#4f8207"}>
              {t(m.sursa === "fem"
                ? "struct.arpl.fem_guverneaza_diag" : "struct.arpl.fasii_guverneaza")}
            </text>
          </g>
        ) : (
          <text x={x0} y={H - 12} fontSize="10" fill="#737373">
            {t("struct.arpl.numai_fasii")}
          </text>
        )}
      </svg>
      <div className="sg-muted" style={{ fontSize: 12 }}>{m.formula}</div>
    </div>
  );
}

// CONSOLA (balconul): schema ei statică e alta — încastrare în placă, armătură
// de sus continuă, plus parapetul de beton la capăt.
function ConsolaDesen({ k }: {
  k: { tip: string; adanc_m: number; L_prindere_m: number;
       armare?: string | null; as_cm2?: number | null };
}) {
  const W = 420, H = 150;
  const xInc = 150, xCapat = W - 40;
  const sus = 60, h = 22;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="ap-svg" style={{ width: "100%", maxWidth: W }}>
      {/* placa din care iese consola */}
      <rect x={40} y={sus} width={xInc - 40} height={h} fill="#dedede" stroke="#232323" />
      {/* consola */}
      <rect x={xInc} y={sus} width={xCapat - xInc} height={h} fill="#ececec" stroke="#232323" />
      {/* armătura de SUS, continuă în placă */}
      <line x1={70} y1={sus + 5} x2={xCapat - 6} y2={sus + 5}
            stroke="#6b4fa1" strokeWidth="3" />
      <text x={xCapat - 6} y={sus - 8} fontSize="10" textAnchor="end" fill="#6b4fa1">
        {k.armare ?? t("struct.arpl.armatura_sus_lipseste")}
        {k.as_cm2 ? ` (${nr(k.as_cm2, 2)} cm²/m)` : ""}
      </text>
      {/* parapetul de beton la capăt */}
      <rect x={xCapat - 8} y={sus - 26} width={8} height={26} fill="#c1c1c1" stroke="#232323" />
      {/* cote */}
      <line x1={xInc} y1={sus + h + 18} x2={xCapat} y2={sus + h + 18}
            stroke="#737373" strokeWidth="1" />
      <text x={(xInc + xCapat) / 2} y={sus + h + 32} fontSize="10"
            textAnchor="middle" fill="#232323">a = {nr(k.adanc_m, 2)} m</text>
      <line x1={40} y1={sus - 16} x2={xInc} y2={sus - 16} stroke="#737373" strokeWidth="1" />
      <text x={(40 + xInc) / 2} y={sus - 20} fontSize="10" textAnchor="middle"
            fill={k.L_prindere_m >= k.adanc_m ? "#4f8207" : "#b5323d"}>
        {t("struct.arpl.prindere_fatada", { v: nr(k.L_prindere_m, 2) })}
      </text>
      <text x={40} y={H - 22} fontSize="10" fill="#737373">
        M = q·a²/2 + parapet·a
      </text>
      <text x={40} y={H - 8} fontSize="10" fill="#737373">
        {t("struct.arpl.armatura_sus_ancorata")}
      </text>
    </svg>
  );
}

// modelul FEM din starea Pasului 7: din el vine GEOMETRIA ochiului (conturul,
// laturile cu reazemul lor, pereții care stau pe placă, golurile). Pasul 16
// ține cifrele, Pasul 7 ține desenul.
function modelFem(st: StepState | null | undefined): FemModel | null {
  return ((st?.result as { model?: FemModel } | null)?.model) ?? null;
}

/** Cardul unui ochi: schema de rezemare, încărcarea, momentele, plasele și
 *  verificările lui. Îl folosesc pagina pasului (ochiul ales) și pagina
 *  elementului (nota de calcul completă). */
export function CardOchi({ ochi: ales, r, panouGeo }: {
  ochi: Ochi;
  r: Rezultat;
  /** geometria ochiului din Pasul 7: perimetrul pe tipuri de reazem */
  panouGeo: FemPanou | null;
}) {
  useT();
  const catalog = useCatalogVerificari();
  return (
        <div className="st-card">
          <h3>
            {ales.id} — {ales.nivel_nume}, {nr(ales.arie_m2, 1)} m²
          </h3>
          {ales.note.length ? (
            <ul className="sg-muted">
              {ales.note.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          ) : null}

          <h4>{t("struct.arpl.schema_de_reazem")}</h4>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.arpl.directie")}</th>
                <th>{t("struct.arpl.th_deschidere")}</th>
                <th>{t("struct.arpl.th_capete_continue")}</th>
                <th>{t("struct.arpl.capatul_i")}</th>
                <th>{t("struct.arpl.capatul_j")}</th>
              </tr>
            </thead>
            <tbody>
              <Capete d={ales.schema.x} nume="X" />
              <Capete d={ales.schema.y} nume="Y" />
            </tbody>
          </table>
          {panouGeo?.laturi?.length ? (
            <div className="sg-muted" style={{ marginTop: 6, lineHeight: 1.55 }}>
              {t("struct.arpl.perimetrul_laturi", {
                p: nr(panouGeo.perimetru_m, 1), n: panouGeo.laturi.length,
              })}{" "}
              {(["grinda", "perete", "contur_fara_reazem"] as const).map((fel, i) => {
                const l = panouGeo.laturi!.filter((x) => x.reazem === fel);
                if (!l.length) return null;
                const L = l.reduce((a, x) => a + x.L_m, 0);
                return (
                  <span key={fel} style={{ color: CULOARE_REAZEM[fel] }}>
                    {i ? " · " : ""}
                    {l.length} × {t(ETICHETA_REAZEM[fel])} = {nr(L, 1)} m
                  </span>
                );
              })}
              {t("struct.arpl.continuitatea_nu")}
            </div>
          ) : null}
          {(["x", "y"] as const).map((d) => (
            <div key={d} style={{ marginTop: 10 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                {t("struct.arpl.directia_dir", { dir: d.toUpperCase() })}
                {ales.schema.dir_scurt === d
                  ? t("struct.arpl.scurta_moment_mare") : ""}
              </div>
              <MomenteDirectie ochi={ales} dir={d} />
            </div>
          ))}

          <h4>{t("struct.arpl.incarcarea")}</h4>
          <table className="sg-table">
            <tbody>
              {ales.incarcare.componente.map((c, i) => (
                <tr key={i}>
                  <td>{c.eticheta}</td>
                  <td>{nr(c.valoare_kn_m2, 2)} kN/m²</td>
                  <td className="sg-muted">
                    {c.formula} — {c.referinta}
                  </td>
                </tr>
              ))}
              <tr>
                <td>
                  <strong>q_Ed (ULS)</strong>
                </td>
                <td>
                  <strong>{nr(ales.incarcare.q_uls_kn_m2, 2)} kN/m²</strong>
                </td>
                <td className="sg-muted">{ales.incarcare.formula_uls}</td>
              </tr>
            </tbody>
          </table>
          {ales.incarcare.linii.length ? (
            <div className="sg-muted">
              {t("struct.arpl.pereti_pe_ochi")}{" "}
              {ales.incarcare.linii
                .map(
                  (l) =>
                    `${nr(l.L_m, 1)} m × ${nr(l.q_ed_kn_ml, 1)} kN/ml (${
                      l.sursa
                    })`
                )
                .join(" · ")}
            </div>
          ) : null}

          <h4>{t("struct.arpl.momentele_de_proiectare")}</h4>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.arpl.directie")}</th>
                <th>k</th>
                <th>l</th>
                <th>{t("struct.arpl.coeficienti")}</th>
                <th>{t("struct.arpl.m_camp")}</th>
                <th>{t("struct.arpl.th_m_reazem_i")}</th>
                <th>{t("struct.arpl.th_m_reazem_j")}</th>
              </tr>
            </thead>
            <tbody>
              {(["x", "y"] as const).map((d) => {
                const m = ales.momente[d];
                if (!m) {
                  return (
                    <tr key={d}>
                      <td>{d.toUpperCase()}</td>
                      <td colSpan={6} className="sg-muted">
                        {t("struct.arpl.directia_duce_incarcare")}
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={d}>
                    <td>{d.toUpperCase()}</td>
                    <td>{nr(m.k, 3)}</td>
                    <td>
                      {nr(m.l_m, 2)} m
                      {m.de_la_vecin ? (
                        <span className="st-warn">
                          {" "}
                          {t("struct.arpl.reazem_de_la_vecin", { v: nr(m.de_la_vecin, 2) })}
                        </span>
                      ) : null}
                    </td>
                    <td className="sg-muted">
                      {m.coef.map((c) => nr(c, 4)).join(" / ")}
                    </td>
                    <td>
                      {nr(m.m_camp_knm_m, 2)}
                      {m.fem ? (
                        <div className="sg-muted">
                          {t("struct.arpl.fasii_fem_scurt", {
                            a: m.fasii ? nr(m.fasii.m_camp_knm_m, 2) : "—",
                            b: nr(m.fem.m_camp_knm_m, 2),
                          })}
                          {m.sursa === "fem" ? (
                            <span className="st-warn"> {t("struct.arpl.fem_guverneaza")}</span>
                          ) : null}
                        </div>
                      ) : null}
                    </td>
                    <td>{nr(m.m_i_knm_m, 2)}</td>
                    <td>{nr(m.m_j_knm_m, 2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <h4>{t("struct.arpl.plasele_alese")}</h4>
          <SectiunePlaca
            ochi={ales}
            acoperire_mm={Number(r.parametri["acoperire_mm"] ?? 20)}
          />
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.arpl.pozitie")}</th>
                <th>{t("struct.arpl.th_armare")}</th>
                <th>A_s</th>
                <th>{t("struct.arf.th_as_necesar")}</th>
                <th>{t("struct.arpl.th_as_minim")}</th>
                <th>d</th>
                <th>M_Rd</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(ales.armare).map(([k, a]) => (
                <tr key={k}>
                  <td>{a.poz}</td>
                  <td>
                    {a.text}
                    {a.imposibil ? (
                      <span className="st-bad"> {t("struct.arpl.peste_gama")}</span>
                    ) : null}
                  </td>
                  <td>{nr(a.as_cm2, 2)} cm²/m</td>
                  <td>{a.as_nec_cm2 === null ? "—" : nr(a.as_nec_cm2, 2)}</td>
                  <td>{nr(a.as_min_cm2, 2)}</td>
                  <td>{nr(a.d_cm, 1)} cm</td>
                  <td>{nr(a.m_rd_knm_m, 2)} kNm/m</td>
                </tr>
              ))}
            </tbody>
          </table>
          {ales.istoric.length ? (
            <div className="sg-muted">
              {t("struct.arpl.incercari")}{" "}
              {ales.istoric
                .map((h) => t("struct.arpl.istoric_rand", {
                  de_la: h.de_la, la: h.la, de_ce: h.de_ce, re: nr(h.re, 2),
                }))
                .join(" · ")}
            </div>
          ) : null}

          {ales.console.length ? (
            <>
              <h4>{t("struct.arpl.consolele_ochiului")}</h4>
              <div className="sg-muted" style={{ marginBottom: 6 }}>
                {t("struct.arpl.consola_are_alta")}
              </div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                {ales.console.map((k, i) => (
                  <div key={i} style={{ flex: "1 1 380px", minWidth: 0 }}>
                    <ConsolaDesen k={k} />
                  </div>
                ))}
              </div>
            </>
          ) : null}

          <h4>{t("struct.arpl.verificarile")}</h4>
          <table className="sg-table">
            <tbody>
              {ales.verificari.filter((v) => !eDecizie(v)).map((v, i) => (
                <tr key={i}>
                  <td style={{ maxWidth: 420 }}>
                    <TitluVerificare
                      catalog={catalog}
                      pas="armare_placa"
                      nume={v.nume}
                      grupa={v.grupa}
                      aratPasul={false}
                    />
                    {v.nota ? (
                      <div className="sg-muted">{v.nota}</div>
                    ) : null}
                  </td>
                  <td>{v.punct ?? "—"}</td>
                  <td>
                    E = {nr(v.E, 2)} {v.unitate ?? ""}
                  </td>
                  <td>
                    R = {nr(v.R, 2)} {v.unitate ?? ""}
                  </td>
                  <td style={{ minWidth: 120 }}>
                    R/E = {nr(v.RE, 2)}
                    {v.RE != null && (
                      <BaraRE re={v.RE} prag={Number(r.parametri["prag_re_acceptare"] ?? 1)} />
                    )}
                  </td>
                  <td className={verdictClass(v.verdict)}>{textVerdict(v.verdict)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {ales.verificari.some(eDecizie) && (
            <div style={{ marginTop: 10 }}>
              <DeciziiProiect decizii={ales.verificari.filter(eDecizie)} />
            </div>
          )}
        </div>
  );
}

export default function StepArmarePlaca({
  state,
  femState,
  running,
  focus,
  projectId,
  solutie,
}: {
  state: StepState | null;
  femState?: StepState | null;
  running: boolean;
  focus?: Focus;
  projectId?: string;
  solutie?: string | null;
}) {
  useT();
  const r = (state?.result ?? null) as Rezultat | null;
  const [sel, setSel] = useState<string | null>(null);

  const ochiuri = useMemo(() => r?.panouri ?? [], [r]);
  const model = modelFem(femState);

  // linkul dintr-o verificare picată („?focus=OP3.1") selectează ochiul
  useEffect(() => {
    if (!focus?.ref) return;
    if (ochiuri.some((o) => o.id === focus.ref)) setSel(focus.ref);
  }, [focus?.ref, focus?.n, ochiuri]);


  if (running) {
    return (
      <div className="st-status">
        <div className="st-spinner" />
        <div>
          <strong>{t("struct.arpl.pasul_ruleaza")}</strong>
          <div className="sg-muted">
            {t("struct.arpl.dimensioneaza_ochiurile")}
          </div>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="sg-muted st-empty">
        {t("struct.arpl.pasul_nu_rulat")}
        <DescriereaPasului cheie="armare_placa" />
      </div>
    );
  }

  if (!r || !r.activ) {
    return (
      <div className="sg-muted st-empty">
        {r?.motiv ?? t("struct.arf.fara_rezultate")}
        <DescriereaPasului cheie="armare_placa" />
      </div>
    );
  }

  const ales = ochiuri.find((o) => o.id === sel) ?? ochiuri[0] ?? null;
  // geometria ochiului ales, din Pasul 7 (contur, laturi, pereți, goluri).
  // Căutare simplă, nu `useMemo`: aici suntem DUPĂ return-urile de mai sus,
  // unde un hook ar fi apelat condiționat.
  const panouGeo: FemPanou | null =
    (model?.panouri ?? []).find((p) => p.id === ales?.id) ?? null;
  const console_ = ochiuri.flatMap((o) =>
    o.console.map((k) => ({ ...k, ochi: o.id, nivel: o.nivel_nume }))
  );
  const tr = r.transfer_diafragma;

  return (
    <div className="st-wrap">
      <div className="st-meta">
        <span>
          <strong>{r.nr_panouri}</strong> {t("struct.arpl.ochiuri_de_placa")}{" "}
          <strong>{r.nr_verificari}</strong> {t("struct.arpl.verificari_dot")}{" "}
          <span className={r.nr_picate ? "st-bad" : "st-ok"}>
            {t("struct.arpl.n_picate", { n: r.nr_picate })}
          </span>{" "}
          {t("struct.arpl.placa_de_cm", {
            v: nr((r.parametri.grosime_placa_m as number) * 100, 0),
            otel: nr(r.otel_total_kg, 0), beton: nr(r.beton_total_m3, 1),
          })}
        </span>
        <span className="sg-muted">{t("struct.arf.rulat", { data: fmtDate(state.updated_at) })}</span>
      </div>

      {r.metoda_momente ? (
        <div className="sg-muted" style={{ marginBottom: 8 }}>
          {r.metoda_momente.fem_activ ? (
            <>
              {t("struct.arpl.momentele_se_iau_pe")} <strong>{t("struct.arpl.infasuratoarea")}</strong> celor două
              {t("struct.arpl.metode_fem", {
                n: r.metoda_momente.nr_taieturi,
                noc: r.metoda_momente.nr_ochiuri_pe_fem,
              })}
            </>
          ) : (
            <>
              {t("struct.arpl.numai_metoda_fasii")}{" "}
              {r.metoda_momente.motiv}
            </>
          )}
        </div>
      ) : null}

      {r.avertismente.length ? (
        <div className="st-warnbox">
          {r.avertismente.map((a, i) => (
            <div key={i}>{a}</div>
          ))}
        </div>
      ) : null}

      <h3>{t("struct.arpl.ochiurile_placa")}</h3>
      <div className="sg-muted" style={{ marginBottom: 8 }}>
        {t("struct.arpl.ochi_panoul_placa")}
      </div>

      <div className="st-findings" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center",
                      flexWrap: "wrap", padding: "8px 12px" }}>
          <label className="sg-muted">
            {t("struct.arpl.ochiul")}{" "}
            <select value={ales?.id ?? ""}
                    onChange={(e) => setSel(e.target.value || null)}>
              {ochiuri.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.id} ({o.nivel_nume}) — {nr(o.arie_m2, 1)} m², {o.verdict}
                </option>
              ))}
            </select>
          </label>
          {ales ? (
            <>
              <span className={verdictClass(ales.verdict)}>{textVerdict(ales.verdict)}</span>
              <span className="sg-muted">
                {t("struct.arpl.rezumat_ochi", {
                  arie: nr(ales.arie_m2, 1), cat: ales.cat,
                  h: nr(ales.h_cm, 0), d: nr(ales.deschidere_m, 2),
                  schema: SCHEMA_LABEL[ales.schema.tip]
                    ? t(SCHEMA_LABEL[ales.schema.tip]) : ales.schema.tip,
                  raport: ales.schema.raport
                    ? t("struct.arpl.raport_l", { v: nr(ales.schema.raport, 2) })
                    : "",
                  q: nr(ales.incarcare.q_uls_kn_m2, 2),
                  otel: nr(ales.otel_kg, 0),
                })}
              </span>
            </>
          ) : null}
        </div>
        {/* planul ochiului + «unde e în clădire», una lângă alta: din desenul
            de detaliu nu se vede la ce nivel și pe ce fațadă ești */}
        <div style={{ display: "flex", alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 520px", minWidth: 0, padding: "0 12px 8px" }}>
            {ales ? (
              <PlanOchi ochi={ales} panou={panouGeo} peVecin={(id) => setSel(id)} />
            ) : null}
          </div>
          <div style={{ flex: "0 0 340px", maxWidth: "100%",
                        borderLeft: "1px solid #dedede" }}>
            <LocalizareCladire
              model={model}
              evidentiate={ales ? [ales.id] : []}
              height={420}
              projectId={projectId}
              solutie={solutie}
              pasCurent="armare_placa"
              peElement={(ref) => setSel(ref)}
              cuPanouri
            />
          </div>
        </div>
        <div className="sg-muted" style={{ padding: "6px 12px", fontSize: 12 }}>
          {t("struct.arpl.dreapta_cladirea_intreag")}
        </div>
      </div>

      {ales ? <CardOchi ochi={ales} r={r} panouGeo={panouGeo} /> : null}

      <h3>{t("struct.arpl.consolele_balcoanele")}</h3>
      {console_.length ? (
        <table className="sg-table">
          <thead>
            <tr>
              <th>{t("struct.arpl.th_ochi")}</th>
              <th>{t("struct.top.nivel")}</th>
              <th>{t("insp.rand.tip")}</th>
              <th>{t("struct.arpl.iesire")}</th>
              <th>{t("struct.arpl.prindere_placa")}</th>
              <th>{t("struct.arpl.armatura_sus")}</th>
            </tr>
          </thead>
          <tbody>
            {console_.map((k, i) => (
              <tr key={i}>
                <td>{k.ochi}</td>
                <td>{k.nivel}</td>
                <td>{k.tip}</td>
                <td>{nr(k.adanc_m, 2)} m</td>
                <td>{nr(k.L_prindere_m, 2)} m</td>
                <td>
                  {k.armare ?? "—"}
                  {k.as_cm2 ? (
                    <span className="sg-muted"> ({nr(k.as_cm2, 2)} cm²/m)</span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="sg-muted">
          {t("struct.arpl.nicio_consola_pasul")}
        </div>
      )}

      <h3>{t("struct.arpl.transferul_diafragma_per")}</h3>
      {tr.activ ? (
        <>
          <div className="sg-muted" style={{ marginBottom: 6 }}>
            {tr.nota}
          </div>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("insp.familie.wall")}</th>
                <th>{t("struct.top.nivel")}</th>
                <th>{t("struct.fem.lungime")}</th>
                <th>V_Ed</th>
                <th>τ_Ed</th>
                <th>τ_Rd = 1,5·f_ctd</th>
                <th>{t("struct.arpl.armatura_conectare")}</th>
                <th>{t("struct.arg.th_verdict")}</th>
              </tr>
            </thead>
            <tbody>
              {tr.pereti.map((w, i) => {
                const v = tr.verificari[i];
                return (
                  <tr key={w.element}>
                    <td>{w.element}</td>
                    <td>{w.nivel}</td>
                    <td>{nr(w.L_m, 2)} m</td>
                    <td>{nr(w.V_ed_kn, 0)} kN</td>
                    <td>{nr(w.tau_ed_kpa, 0)} kPa</td>
                    <td>{nr(w.tau_rd_kpa, 0)} kPa</td>
                    <td>{nr(w.as_conector_cm2_m, 2)} cm²/m</td>
                    <td className={verdictClass(v?.verdict ?? "")}>
                      {v?.verdict ?? "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      ) : (
        <div className="sg-muted">
          {t("struct.arpl.nu_se_aplica", { motiv: traduceRezultat(tr.motiv) })}
        </div>
      )}

      <h3>{t("struct.arpl.strapungerea")}</h3>
      {r.strapungere?.activ ? (
        r.strapungere.stalpi.length ? (
          <>
            <div className="sg-muted" style={{ marginBottom: 6 }}>
              {r.strapungere.nota}
            </div>
            <table className="sg-table">
              <thead>
                <tr>
                  <th>{t("struct.arpl.stalp")}</th>
                  <th>{t("struct.top.nivel")}</th>
                  <th>{t("struct.arpl.th_salt_axial")}</th>
                  <th>{t("struct.arpl.th_adus_grinzi")}</th>
                  <th>{t("struct.arpl.v_ed_prin")}</th>
                  <th>{t("struct.arpl.pozitie")}</th>
                  <th>u₁</th>
                  <th>v_Ed</th>
                  <th>v_Rd,c</th>
                  <th>{t("struct.arg.th_verdict")}</th>
                </tr>
              </thead>
              <tbody>
                {r.strapungere.stalpi.map((w, i) => {
                  const v = r.strapungere.verificari[2 * i];
                  return (
                    <tr key={`${w.id_plan}-${w.nivel}`}>
                      <td>{w.id_plan}</td>
                      <td>{w.nivel}</td>
                      <td>{nr(w.salt_axial_kn, 0)} kN</td>
                      <td>{nr(w.adus_de_grinzi_kn, 0)} kN</td>
                      <td>
                        <strong>{nr(w.v_ed_kn, 0)} kN</strong>
                      </td>
                      <td>
                        {w.poz}
                        <span className="sg-muted"> β={nr(w.beta, 2)}</span>
                      </td>
                      <td>{nr(w.u1_m, 2)} m</td>
                      <td>{nr(w.v_ed_mpa, 3)} MPa</td>
                      <td>{nr(w.v_rdc_mpa, 3)} MPa</td>
                      <td className={verdictClass(v?.verdict ?? "")}>
                        {v?.verdict ?? "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        ) : (
          <div className="sg-muted">
            {t("struct.arpl.niciun_stalp")}
            {r.strapungere.nr_sarite
              ? t("struct.arpl.p1_stalpi", { p1: r.strapungere.nr_sarite })
              : ""}
            .
          </div>
        )
      ) : (
        <div className="sg-muted">
          {t("struct.arpl.nu_se_aplica", { motiv: traduceRezultat(r.strapungere?.motiv) })}
        </div>
      )}

      {r.zone_gol && r.zone_gol.length ? (
        <>
          <h3>{t("struct.arpl.ochiuri_casa_scarii")}</h3>
          <div className="sg-muted" style={{ marginBottom: 6 }}>
            {t("struct.arpl.acolo_placa_are")}
          </div>
          <table className="sg-table">
            <tbody>
              {r.zone_gol.map((z, i) => (
                <tr key={i}>
                  <td>{z.ochi}</td>
                  <td>{z.nivel}</td>
                  <td>{z.eticheta}</td>
                  <td>
                    {t("struct.arpl.arie_din", { a: nr(z.arie_pe_ochi_m2, 1), b: nr(z.arie_zona_m2, 1) })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}

      <h3>{t("struct.arpl.ipotezele_metodei")}</h3>
      <table className="sg-table">
        <tbody>
          {r.ipoteze.map((ip, i) => (
            <tr key={i}>
              <td style={{ maxWidth: 360 }}>{ip.ce}</td>
              <td className="sg-muted">{ip.de_ce}</td>
              <td className="sg-muted">{ip.efect}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3>{t("struct.arpl.verifica_pasul_inca")}</h3>
      <table className="sg-table">
        <tbody>
          {r.nu_se_aplica.map((n, i) => (
            <tr key={i}>
              <td>
                {n.nume} <span className="sg-muted">→ {n.la}</span>
              </td>
              <td className="sg-muted">{n.motiv}</td>
              <td className="sg-muted">{n.referinta}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <Info
        titlu={t("struct.arpl.unde_vin_momentele")}
        ce={t("struct.arpl.unde_vin_ce")}
        deCe={t("struct.arpl.unde_vin_de_ce")}
      />

      {/* notele se adună de la toate ochiurile, deci fiecare își spune
          numele — altfel „18 tronsoane de perete stau PE ochi" nu se știe
          despre care ochi vorbește */}
      <NotePas
        note={ochiuri.flatMap((o) => o.note.map((n) => `${o.id}: ${n}`))}
      />
    </div>
  );
}
